import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { InfoTipPopover } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { TextLink, Tray, Truncated, useModalControl, Text } from '@frontend/design-system';
import { ROUTING_ANALYTICS_ID } from '../routing-analytics-panel';
import { DevicePreviewModal } from './device-preview-modal';
import { labels, rateLabels, chartColumnHeaders } from './labels';
import { AnalyticsDataKey, GroupItemsProp } from './types';
import { formatPercentage } from './utils';
import { SubduedText } from '.';

const colors = {
  totalCalls: theme.colors.primary50,
  answeredCalls: theme.colors.success30,
  missedCalls: theme.colors.critical30,
  abandonedCalls: theme.colors.warning20,
} as const;

const tooltipColors = {
  totalCalls: theme.colors.primary70,
  answeredCalls: theme.colors.success50,
  missedCalls: theme.colors.critical50,
  abandonedCalls: theme.colors.warning40,
} as const;

export const CallVolumeChart = ({
  filterHintText,
  customNameHeaders,
  entityType,
  categoryBarChartData,
  title,
  children,
}: GroupItemsProp) => {
  const { t } = useTranslation('analytics-phone');

  const { modalProps, triggerProps } = useModalControl();
  const [modalState, setModalState] = useState<{ id: string; name: string }>({ id: '', name: '' });

  const modifiedChartColumnHeaders = useMemo(() => {
    return Object.entries(chartColumnHeaders).reduce((acc, [key, value]) => {
      if (key === 'groupNameHeader') return acc;
      if (key === AnalyticsDataKey.TotalCalls) {
        acc[AnalyticsDataKey.TotalCalls] = t('{{ value }} to {{ entityType }}', {
          value,
          entityType,
        });
      } else {
        acc[key as keyof typeof chartColumnHeaders] = t('{{ value }} by {{ entityType }}', {
          value,
          entityType,
        });
      }
      return acc;
    }, {} as Record<keyof typeof chartColumnHeaders, string>);
  }, [entityType, t]);

  const lowerCaseEntityType = entityType.toLowerCase();

  return (
    <>
      <Chart
        colors={colors}
        labels={labels}
        showOverflow
        emptyStateConfig={{
          type: 'analytics',
          label: t('No data available'),
        }}
      >
        <Chart.Header
          title={title}
          subtitle={filterHintText}
          bottomElement={<SubduedText color='subdued'>{t('Hover over each bar to learn more')}</SubduedText>}
        />
        {children ? (
          children
        ) : (
          <>
            <Chart.CategoryBarChart
              data={categoryBarChartData}
              customGroupNameRenderer={({ groupName: id }) => {
                return (
                  <>
                    <Truncated as={Text} size='medium'>
                      {customNameHeaders[id].name}
                    </Truncated>
                    {customNameHeaders[id].devicesCount > 0 && (
                      <TextLink
                        onClick={() => {
                          triggerProps.onClick();
                          setModalState({
                            id,
                            name: customNameHeaders[id].name,
                          });
                        }}
                        trackingId={`${ROUTING_ANALYTICS_ID}-'call${lowerCaseEntityType}-view${lowerCaseEntityType}devicelist`}
                        size='small'
                        css={{ cursor: 'pointer' }}
                      >
                        {t('{{ count }} Devices', { count: customNameHeaders[id].devicesCount })}
                      </TextLink>
                    )}
                  </>
                );
              }}
              appearance={{
                mode: 'grouped',
                defaultRowsCount: 10,
                showSerials: false,
                groupNameWidth: 80,
                customTooltipTitle: ({ groupName: id }) => customNameHeaders[id].name,
                collectiveTooltip: true,
                customTooltipData: (groupId, categoryId) => {
                  const id = categoryId as keyof typeof tooltipColors;
                  const groupData = categoryBarChartData[groupId];

                  if (!groupData) {
                    return [
                      {
                        color: tooltipColors[id],
                        formattedValue: '0',
                        label: labels[categoryId as keyof typeof labels] ?? categoryId,
                        id: categoryId,
                      },
                    ];
                  }

                  const value = groupData[id];
                  const baseLabel = labels[categoryId as keyof typeof labels];
                  const tooltipData = [
                    {
                      color: tooltipColors[id],
                      formattedValue: value.toString(),
                      label:
                        categoryId === AnalyticsDataKey.TotalCalls
                          ? t('{{ label }} to this {{ entityType }}', { label: baseLabel, entityType })
                          : t('{{ label }} by this {{ entityType }}', { label: baseLabel, entityType }),
                      id: categoryId,
                    },
                  ];

                  if (categoryId !== 'totalCalls' && groupData.totalCalls > 0) {
                    tooltipData.push({
                      color: tooltipColors[id],
                      formattedValue: formatPercentage(value, groupData.totalCalls),
                      label: t('{{ label }} for this {{ entityType }}', {
                        label: rateLabels[categoryId as keyof typeof rateLabels],
                        entityType,
                      }),
                      id: categoryId,
                    });
                  }

                  return tooltipData;
                },
                alternateCategoryBackgroundColor: theme.colors.neutral5,
                customCategoryNameRenderer: (categoryId: string) => {
                  if (categoryId === 'groupNameHeader')
                    return (
                      <div
                        css={css`
                          margin-bottom: ${theme.spacing(5)};
                        `}
                      ></div>
                    );
                  return (
                    <div
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                      }}
                    >
                      <Text textAlign='center' size='medium'>
                        {modifiedChartColumnHeaders[categoryId as keyof typeof modifiedChartColumnHeaders] ??
                          categoryId}
                      </Text>
                      {categoryId === AnalyticsDataKey.MissedCalls && (
                        <InfoTipPopover
                          trackingId={`${ROUTING_ANALYTICS_ID}-'call${lowerCaseEntityType}-missedcallsinfoicon`}
                        >
                          {t(
                            'These calls were missed by the Call {{ entityType }}, but may have been answered later in the call route.',
                            { entityType }
                          )}
                        </InfoTipPopover>
                      )}
                    </div>
                  );
                },
              }}
            />
            {!!categoryBarChartData && (
              <SubduedText weight='bold' css={{ display: 'flex', justifyContent: 'center' }}>
                {t('Count of Calls')}
              </SubduedText>
            )}
          </>
        )}
      </Chart>
      <Tray {...modalProps} width='medium'>
        <DevicePreviewModal entityType={entityType} modalState={modalState} onClose={modalProps.onClose} />
      </Tray>
    </>
  );
};
